import { useState } from "react";
import { Play, Pause, Square, SkipForward, Timer, Coffee, MapPin, HelpCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useWorkTimerContext } from "./WorkTimerProvider";
import type { TimerMode } from "@/hooks/useWorkTimer";

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

const COUNTDOWN_PRESETS = [15, 25, 30, 45, 60, 90, 120];

interface WorkTimerProps {
  onClose?: (options?: { animate?: boolean }) => void;
  cafeId?: string;
  cafeName?: string;
}

export function WorkTimer({ onClose, cafeId, cafeName: cafeNameProp }: WorkTimerProps) {
  const {
    timerState,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    skipPhase,
    setMode,
    setCountdownDuration,
    getPhaseLabel,
  } = useWorkTimerContext();

  const [selectedMode, setSelectedMode] = useState<TimerMode>(timerState.mode);
  const [selectedDuration, setSelectedDuration] = useState(timerState.targetMinutes || 25);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [focusRating, setFocusRating] = useState<number | null>(null);
  

  const { phase, timeRemaining, isRunning, cyclesCompleted, totalFocusSeconds, totalBreakSeconds, isPaused, cafeName } = timerState;

  // Circular progress
  const totalPhaseTime = phase === "focus"
    ? (timerState.mode === "pomodoro" ? 25 * 60 : (timerState.targetMinutes || 25) * 60)
    : phase === "break" ? 5 * 60 : 15 * 60;
  const progress = totalPhaseTime > 0 ? ((totalPhaseTime - timeRemaining) / totalPhaseTime) * 100 : 0;
  const circumference = 2 * Math.PI * 90;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const phaseColors = {
    idle: "stroke-muted",
    focus: "stroke-sage",
    break: "stroke-honey",
    long_break: "stroke-terracotta",
  };

  const handleStart = async () => {
    await startTimer({
      mode: selectedMode,
      targetMinutes: selectedMode === "countdown" ? selectedDuration : undefined,
      cafeId,
      cafeName: cafeNameProp,
    });
    setTimeout(() => {
      onClose?.({ animate: true });
    }, 1500);
  };

  const handleStop = async () => {
    await stopTimer(focusRating ?? undefined);
    setShowStopConfirm(false);
    setFocusRating(null);
    onClose?.();
  };

  const handleModeChange = (m: TimerMode) => {
    setSelectedMode(m);
    setMode(m);
  };

  // Stop confirmation dialog
  if (showStopConfirm) {
    return (
      <div className="flex flex-col items-center gap-6 py-6 px-4">
        <h3 className="font-display text-xl font-semibold text-foreground">End Session?</h3>
        <p className="text-sm text-muted-foreground text-center">
          You focused for <span className="font-semibold text-foreground">{formatDuration(totalFocusSeconds)}</span>
          {cyclesCompleted > 0 && <> with <span className="font-semibold text-foreground">{cyclesCompleted}</span> pomodoro cycles</>}.
        </p>

        <div className="flex flex-col items-center gap-2">
          <p className="text-sm font-medium text-foreground">How was your focus?</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map(r => (
              <button
                key={r}
                onClick={() => setFocusRating(r)}
                className={cn(
                  "w-10 h-10 rounded-full text-sm font-medium transition-all",
                  focusRating === r
                    ? "bg-primary text-primary-foreground scale-110"
                    : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                )}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3 w-full">
          <Button variant="outline" className="flex-1" onClick={() => setShowStopConfirm(false)}>
            Continue
          </Button>
          <Button className="flex-1" onClick={handleStop}>
            End Session
          </Button>
        </div>
      </div>
    );
  }

  // Idle state - setup
  if (!isRunning) {
    return (
      <div className="flex flex-col items-center gap-6 py-6 px-4">
        <div className="flex items-center gap-2">
          <Timer className="h-5 w-5 text-primary" />
          <h3 className="font-display text-xl font-semibold text-foreground">Focus Session</h3>
          <Popover>
            <PopoverTrigger asChild>
              <button className="rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" aria-label="How does this work?">
                <HelpCircle className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="center" className="w-72 text-sm">
              {selectedMode === "pomodoro" ? (
                <div className="space-y-1.5">
                  <p className="font-medium text-foreground">Pomodoro Technique</p>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Work in focused 25-minute blocks, then take a 5-minute break. After 4 cycles, take a longer 15-minute break. Your sessions are logged automatically.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <p className="font-medium text-foreground">Countdown Mode</p>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Set a custom duration for a single focused session — no breaks, just uninterrupted work time. Your focus time is logged automatically.
                  </p>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>

        {/* Mode toggle */}
        <div className="flex bg-secondary rounded-lg p-1 w-full max-w-xs">
          <button
            onClick={() => handleModeChange("pomodoro")}
            className={cn(
              "flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all",
              selectedMode === "pomodoro"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground"
            )}
          >
            Pomodoro
          </button>
          <button
            onClick={() => handleModeChange("countdown")}
            className={cn(
              "flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all",
              selectedMode === "countdown"
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground"
            )}
          >
            Countdown
          </button>
        </div>

        {/* Pomodoro info */}
        {selectedMode === "pomodoro" && (
          <div className="text-center text-sm text-muted-foreground">
            <p>25 min focus → 5 min break</p>
            <p className="text-xs mt-1">Long break every 4 cycles</p>
          </div>
        )}

        {/* Countdown duration selector */}
        {selectedMode === "countdown" && (
          <div className="flex flex-wrap justify-center gap-2">
            {COUNTDOWN_PRESETS.map(m => (
              <button
                key={m}
                onClick={() => { setSelectedDuration(m); setCountdownDuration(m); }}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium transition-all",
                  selectedDuration === m
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                )}
              >
                {m >= 60 ? `${m / 60}h` : `${m}m`}
              </button>
            ))}
          </div>
        )}

        <Button onClick={handleStart} size="lg" className="gap-2 w-full max-w-xs">
          <Play className="h-5 w-5" />
          Start Session
        </Button>
      </div>
    );
  }

  // Running state
  return (
    <div className="flex flex-col items-center gap-4 py-4 px-4">
      {/* Cafe context */}
      {cafeName && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" />
          <span>{cafeName}</span>
        </div>
      )}

      {/* Phase label */}
      <div className={cn(
        "px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider",
        phase === "focus" ? "bg-sage/15 text-sage" : "bg-honey/15 text-honey",
      )}>
        {getPhaseLabel()}
      </div>

      {/* Circular progress */}
      <div className="relative w-52 h-52">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
          <circle
            cx="100" cy="100" r="90"
            fill="none"
            className="stroke-muted/30"
            strokeWidth="6"
          />
          <circle
            cx="100" cy="100" r="90"
            fill="none"
            className={cn("transition-all duration-1000", phaseColors[phase])}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-mono text-4xl font-bold text-foreground tabular-nums">
            {formatTime(timeRemaining)}
          </span>
          {timerState.mode === "pomodoro" && (
            <span className="text-xs text-muted-foreground mt-1">
              Cycle {cyclesCompleted + (phase === "focus" ? 1 : 0)} of 4
            </span>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        {isPaused ? (
          <Button onClick={resumeTimer} size="lg" className="gap-2">
            <Play className="h-5 w-5" />
            Resume
          </Button>
        ) : (
          <Button onClick={pauseTimer} variant="secondary" size="lg" className="gap-2">
            <Pause className="h-5 w-5" />
            Pause
          </Button>
        )}
        {(phase === "break" || phase === "long_break") && (
          <Button onClick={skipPhase} variant="outline" size="lg" className="gap-2">
            <SkipForward className="h-5 w-5" />
            Skip
          </Button>
        )}
        <Button onClick={() => setShowStopConfirm(true)} variant="outline" size="lg" className="gap-2 text-destructive hover:text-destructive">
          <Square className="h-4 w-4" />
          Stop
        </Button>
      </div>

      {/* Session stats */}
      <div className="flex gap-6 pt-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-sage" />
          Focus: {formatDuration(totalFocusSeconds)}
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-honey" />
          Break: {formatDuration(totalBreakSeconds)}
        </div>
      </div>
    </div>
  );
}
